import { ChangeEvent, useEffect, useState } from "react";
import { FormControl, FormHelperText, FormLabel, Textarea, Typography } from "@financeable-com-au/financeable-ui";

export interface CommentBoxProps {
  /** Ties the label to the textarea, so it has to be unique on the page. */
  id: string;
  /** Visible label. Left out, the placeholder names the field for screen readers instead. */
  label?: string;
  /** The stored comment. The field follows it, including one that loads after the first render. */
  value?: string | null;
  placeholder?: string;
  helperText?: string;
  /** Caps the comment length and shows a character count under the field. */
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  disabled?: boolean;
  /** Receives the trimmed comment on blur, only when it differs from the stored one. */
  onSave: (comment: string) => void;
}

const DEFAULT_MIN_ROWS = 3;
const COUNTER_TEXT_COLOR = "neutral.500";
const COUNTER_STYLE = { marginLeft: "auto" };

/**
 * Full width comment field for free text held against a record. It keeps the draft locally while it is
 * being typed and hands the trimmed comment to `onSave` on blur, so the caller saves once per edit.
 */
const CommentBox = ({
  id,
  label,
  value,
  placeholder,
  helperText,
  maxLength,
  minRows = DEFAULT_MIN_ROWS,
  maxRows,
  disabled = false,
  onSave,
}: CommentBoxProps) => {
  const savedComment = value ?? "";
  const [comment, setComment] = useState(savedComment);

  useEffect(() => {
    setComment(savedComment);
  }, [savedComment]);

  const handle = {
    onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
      setComment(event.target.value);
    },
    onBlur: () => {
      const trimmedComment = comment.trim();
      setComment(trimmedComment);

      // The stored comment is trimmed for the comparison too. A record saved with surrounding whitespace
      // would otherwise never match the trimmed draft, and every focus and blur would fire another save.
      if (trimmedComment === savedComment.trim()) return;

      onSave(trimmedComment);
    },
  };

  return (
    <FormControl>
      {label ? <FormLabel htmlFor={id}>{label}</FormLabel> : null}
      <Textarea
        name={id}
        variant="soft"
        value={comment}
        placeholder={placeholder}
        minRows={minRows}
        maxRows={maxRows}
        disabled={disabled}
        onChange={handle.onChange}
        onBlur={handle.onBlur}
        // The id belongs on the textarea itself, so the label points at the field and not at its wrapper.
        // Without a visible label the placeholder carries the accessible name.
        slotProps={{ textarea: { id, maxLength, "aria-label": label ? undefined : placeholder } }}
        endDecorator={
          maxLength ? (
            <Typography level="body-xs" textColor={COUNTER_TEXT_COLOR} style={COUNTER_STYLE}>
              {`${comment.length} / ${maxLength}`}
            </Typography>
          ) : undefined
        }
      />
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};

export default CommentBox;
